import { useState, useEffect } from 'react';
import type { PermissionGroup, PermissionMetadata } from '../../../../../../typings/api';
import type { RoleScope } from '../../../../../../typings/enums';
import { get } from '../../../../../redux/api';
import type { RolePermission } from '../../../../../../shared/model-typings';

//  TYPES

export type AllPermissionsItem = PermissionMetadata & {
  id: string;
  isChecked: boolean;
};

export type PermissionsByGroup = Record<PermissionGroup, AllPermissionsItem[]>;

type PermissionsEntries = Array<[string, AllPermissionsItem[]]>;

type UseAllPermissionsProps = {
  isReadOnly: boolean;
  scope: RoleScope;
  rolePermissions: RolePermission[];
  updatePermissions: (updatedPermissions: AllPermissionsItem[]) => void;
  setEditingPermissions: (updatedPermissions: AllPermissionsItem[]) => void;
};

type UseAllPermissionsState = {
  isFetching: boolean;
  searchQuery: string;
  filteredPermissions: PermissionsByGroup; // Displays the filtered list of permissions
  isAllPermissionsChecked: boolean;
  permissionsSelected: {
    all: number;
    hidden: number;
  };
};

type UseAllPermissionsDispatch = {
  onSearchQueryChange: (updatedSearchQuery: string) => void;
  onTogglePermission: (permissionId: string) => void;
  onToggleGroup: (group: PermissionGroup) => void;
  onToggleAll: () => void;
};

type UseAllPermissions = {
  state: UseAllPermissionsState;
  dispatch: UseAllPermissionsDispatch;
  groups: PermissionsEntries;
};

//  Helpers, passed to array methods like .every()
export const permissionIsChecked = (permission: AllPermissionsItem) => permission.isChecked;

//  Check if a permission has a given scope.
const permissionHasScope = (permission: AllPermissionsItem, scope: RoleScope) => permission.scopes.includes(scope);

//  Determine if a permission should be displayed based on the search query.
const permissionMatchesSearchQuery = (permission: AllPermissionsItem, query: string) => {
  if (!query || permission.description.toLowerCase().includes(query.toLowerCase())) {
    return true;
  }
  return false;
};

/**
 * Hook for fetching the list of permissions and returning them in their groups.
 */
export default function useAllPermissions({
  scope,
  isReadOnly,
  rolePermissions = [],
  updatePermissions,
  setEditingPermissions,
}: UseAllPermissionsProps): UseAllPermissions {
  const [isFetching, setIsFetching] = useState(false);
  const [allPermissions, setAllPermissions] = useState<AllPermissionsItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  //  Handles persisting the permissions to Redux
  const savePermissionsToRedux = (currentAllPermissions: AllPermissionsItem[]) =>
    updatePermissions(currentAllPermissions.filter(permissionIsChecked));

  //  HELPERS

  //  Method for determining if a given permission is active, if we are viewing a readonly
  const isRolePermissionActive = ({ resourceType, action }: PermissionMetadata): boolean =>
    rolePermissions.some(
      (rolePermission) => resourceType === rolePermission.resourceType && action === rolePermission.action,
    );

  //  Transform a fetched permission into an AllPermissionsItem
  const toAllPermissionItem = (permission: PermissionMetadata): AllPermissionsItem => ({
    ...permission,
    id: `permission-${permission.resourceType}-${permission.action}`, // Used as unique id for keys & state toggling.
    isChecked: isRolePermissionActive(permission),
  });

  //  Returns a function which only calls the provided function if we are not in readonly mode.
  const ifNotReadOnly =
    (fn: (...args: any[]) => void) =>
      (...args: any[]) =>
        !isReadOnly && fn(...args);

  //  Group a given set of permissions, filtering out any items which don't match the search query.
  const groupPermissions = (permissions: AllPermissionsItem[], query: string): PermissionsByGroup =>
    permissions.reduce((groupedPermissions, permission) => {
      //  Return the accumulator if the permission is being filtered by the search query.
      if (!permissionMatchesSearchQuery(permission, query)) {
        return groupedPermissions;
      }
      //  ...otherwise, add this permission to the group it belongs to (creating the group i)
      return {
        ...groupedPermissions,
        [permission.group]: groupedPermissions[permission.group] // Check if the group already exists
          ? groupedPermissions[permission.group].concat(permission)
          : [permission],
      };
    }, {} as PermissionsByGroup);

  const unCheckAllOutOfScopePermissions = (currentScope: RoleScope) =>
    setAllPermissions((prevAllPermissions) =>
      prevAllPermissions.map((permission) => ({
        ...permission,
        isChecked: permissionHasScope(permission, currentScope)
          ? permission.isChecked // If the permission is in the current scope, set isChecked to whatever it was before...
          : false, // ...otherwise uncheck it.
      })),
    );

  //  Method for fetching when component mounts
  async function fetchAllPermissions() {
    setIsFetching(true);
    const resp = await get<PermissionMetadata[]>('/api/roles/all-permissions');
    const permissions = resp.data ?? [];

    //  This adds id + isChecked fields to each permission.
    const allPermissionsItems = permissions.map(toAllPermissionItem);
    setAllPermissions(allPermissionsItems);
    //  If there are rolePermissions from the `roles.entities` store,
    //  they do not have a `scopes` field and so need to be set with those permissions.
    if (rolePermissions.length > 0) {
      //  This will "update" the permissions in `roles.editingRole`
      setEditingPermissions(allPermissionsItems.filter(permissionIsChecked));
    }
    setIsFetching(false);
  }

  //  onMount HOOK

  useEffect(() => {
    //  Fetch if we aren't already, and if allPermissions is yet to be populated.
    if (!isFetching && allPermissions.length === 0) {
      fetchAllPermissions();
    }
    //  Since this hook will re-run on scope change, we need to make sure that out-of-scope permissions are unchecked:
    unCheckAllOutOfScopePermissions(scope);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We're leaving this one as-is, but should check if this should be updated.
  }, [scope]);

  //  DISPATCH METHODS

  const onSearchQueryChange = (updatedSearchQuery: string) => setSearchQuery(() => updatedSearchQuery);

  const onTogglePermission = ifNotReadOnly((permissionId: string) =>
    setAllPermissions((prevAllPermissions) => {
      const updatedPermissions = prevAllPermissions.map((permission) =>
        permission.id !== permissionId
          ? permission
          : {
              ...permission,
              isChecked: !permission.isChecked,
            },
      );
      //  Persist the permissions to Redux, and return to update the local UI state.
      savePermissionsToRedux(updatedPermissions);
      return updatedPermissions;
    }),
  );

  const onToggleGroup = ifNotReadOnly((group: PermissionGroup) => setAllPermissions(prevAllPermissions => {
    const isGroupAllChecked = prevAllPermissions
      .filter(
        (permission) =>
          permission.group === group &&
          permissionHasScope(permission, scope) &&
          permissionMatchesSearchQuery(permission, searchQuery),
      )
      .every(permissionIsChecked);
    const updatedPermissions = prevAllPermissions.map((permission) =>
      permission.group !== group ||
      !permissionMatchesSearchQuery(permission, searchQuery) ||
      !permissionHasScope(permission, scope)
        ? permission
        : {
            ...permission,
            isChecked: !isGroupAllChecked,
          },
    );
    //  Persist the permissions to Redux, and return to update the local UI state.
    savePermissionsToRedux(updatedPermissions);
    return updatedPermissions;
  }));

  const onToggleAll = ifNotReadOnly(() =>
    setAllPermissions((prevAllPermissions) => {
      const isAllCurrentPermissionsChecked = prevAllPermissions
        .filter(
          (permission) =>
            permissionHasScope(permission, scope) && permissionMatchesSearchQuery(permission, searchQuery),
        )
        .every(permissionIsChecked);
      const updatedPermissions = prevAllPermissions.map((permission) =>
        //  If the permission is in scope and matches the search query, it will be one of "all" the permissions we are checking
        permissionHasScope(permission, scope) && permissionMatchesSearchQuery(permission, searchQuery)
          ? {
              ...permission,
              isChecked: !isAllCurrentPermissionsChecked,
            }
          : permission,
      );
      //  Persist the permissions to Redux, and return to update the local UI state.
      savePermissionsToRedux(updatedPermissions);
      return updatedPermissions;
    }),
  );

  //  currentPermissions reflects the current available permissions, based on the current scope.
  const currentPermissions = allPermissions.filter((permission) => permissionHasScope(permission, scope));
  //  Get get all the checked permissions.
  const checkedPermissions = allPermissions.filter(permissionIsChecked);

  const filteredPermissions = groupPermissions(currentPermissions, searchQuery);
  const groups = filteredPermissions !== null ? Object.entries(filteredPermissions) : [];

  //  Returned state + dispatchers
  return {
    state: {
      isFetching,
      searchQuery,
      filteredPermissions,
      // While the consumer could figure this out, it's easier to do here since permissions is a flat list.
      isAllPermissionsChecked: currentPermissions
        .filter((permission) => permissionMatchesSearchQuery(permission, searchQuery))
        .every(permissionIsChecked),
      permissionsSelected: {
        all: checkedPermissions.length,
        hidden: checkedPermissions.filter((permission) => !permissionMatchesSearchQuery(permission, searchQuery))
          .length,
      },
    },
    groups,
    dispatch: {
      onSearchQueryChange,
      onTogglePermission,
      onToggleGroup,
      onToggleAll,
    },
  };
}
